import SubsectionHeader from '../components/subsection-header/subsection-header';
import SubsectionFooter from '../components/subsection-footer/subsection-footer';
import takeawaysSquare from './../assets/images/takeaways-shapes-square.svg';
import takeawaysTriangle from './../assets/images/takeaways-shapes-triangle.svg';
import TakeawaysKeyMotivator from './takeaways-key-motivator';
import TakeawaysSummary from './takeaways-summary';
import { useLocation } from 'react-router-dom';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useState, useEffect, useRef } from 'react';
import { getFacebookUrl, getLinkedinUrl, getTwitterUrl } from "@phntms/react-share";
import './takeaways.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

import facebook from './../assets/images/facebook.svg';
import linkedin from './../assets/images/linkedin.svg';
import twitter from './../assets/images/twitter.svg';
import mail from './../assets/images/mail.svg';
import shapes from './../assets/images/takeaways-shapes.svg';

const keyTakeawaysReferences = [
  {reference: "“Direct Mail Industry Research: Insights Report,” Summit Research for McCann Worldgroup, May 2023."}
];

export default function TakeAways() {

  const url = useLocation();
  const path = url.pathname;
  const segment = path.substring(path.lastIndexOf('/') + 1);
  const currentSection = "Takeaways";
  const keyTakeaways = true;
  // References 
  const [references, setReferences] = useState(keyTakeawaysReferences);

  // Hero Headings
  const h1 = "Key Takeaways";
  const heroAlt = "Shapes of various sizes and colors on a black background.";

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  useEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [url.pathname]);

  useEffect(() => {
    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); /* <- cleanup! */ 
  }, [])

  return (
    <>
      <div className="takeaways">
        <div className='container' data-aos="fade-up">
          <SubsectionHeader currentSection={currentSection} h1={h1} heroAlt={heroAlt} show={false}/>
          <div className='takeaway-row'>
            <div className='left-col'>
              <div className='personalized-box'>
                <h3 id="s1">Discover 6 key takeaways the research revealed.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></h3>
                <div className='shape-images'>
                  <div className='triangle triangle-1'>
                    <img ref={triangle1Ref} src={takeawaysTriangle} alt="" />
                  </div>
                  <div className='square square-1'>
                    <img ref={square1Ref} src={takeawaysSquare} alt="" />
                  </div>
                  <div className='triangle triangle-2'>
                    <img ref={triangle2Ref} src={takeawaysTriangle} alt="" />
                  </div>
                  <div className='square square-2'>
                    <img ref={square2Ref} src={takeawaysSquare} alt="" />
                  </div>
                </div>
              </div>
            </div>
            <div className='right-col'>
              <div className='steps-row'>
                <div className='step-col'>
                  <span className='counter'>1</span>
                  <h2>Overall, direct mail is considered an effective marketing channel.</h2>
                  <p>Companies find mail to be a powerful marketing channel. To get the most mileage out of it, strategic integration is imperative: Integrating mail with digital marketing efforts helps create more actionable, relevant campaigns—giving customers multiple ways to engage and enticing them to act.</p>
                </div>
                <div className='step-col'>
                  <span className='counter'>2</span>
                  <h2>Social media and email are the most frequently used channels.</h2>
                  <p>Among marketers who use direct mail, social and email are their channels of choice (aside from direct mail) and are also considered the most effective. Assess customer behavior, habits and preferences on these channels to inform future campaigns.</p>
                </div>
                <div className='step-col'>
                  <span className='counter'>3</span>
                  <h2>Most companies use direct mail to build brand awareness.</h2>
                  <p>Bold, eye-catching design and clear, to-the-point messaging can help raise awareness of your brand and offerings. And amid the daily onslaught of digital ads, the uniquely tangible, tactile quality of direct mail can set your business apart.</p>
                </div>
                <div className='step-col'>
                  <span className='counter'>4</span>
                  <h2>Postcards are the most popular direct mail format.</h2>
                  <p>Immediate, eye-catching and affordable, postcards are the most frequently used mail format, with 70% of companies surveyed using them. Postcards are ideal for conveying quick messages and announcing sales and events. Create postcards worth displaying so they end up on customers’ refrigerators or desks.</p>
                </div>
                <div className='step-col'>
                  <span className='counter'>5</span>
                  <h2>Mail is typically used as the centerpiece of campaigns.</h2>
                  <p id="s2">As a physical channel, mail can be a powerful campaign centerpiece, creating more personal connections and building trust. Digital innovations such as QR Codes make it easy to connect mail to digital channels such as websites or social platforms, while giving customers multiple ways to engage.</p>
                </div>
                <div className='step-col'>
                  <span className='counter'>6</span>
                  <h2>Most companies plan to maintain or increase mail use.</h2>
                  <p>Most businesses will use mail the same or more going forward. The cost of mail was named as the main obstacle to increasing use. To keep down costs, make sure there are no repeats on mailing lists, so you don’t waste money sending out multiple pieces to the same address (and don’t overwhelm customers).</p>
                </div>
              </div>
            </div>
            
          </div>
          
          <SubsectionFooter currentSection={currentSection} KeyMotivator={TakeawaysKeyMotivator} Summary={TakeawaysSummary} references={references} keyTakeaways={keyTakeaways} />
          
        </div>
      </div>
    </>
  );
}
